import { hydrateWidgetData, type HydratedWidgetResult, type HydrateWidgetInput } from '../api'
import type { DashboardWidget } from '../types'
import { createWidgetRequestIntent } from './engine/intent'
import { buildWidgetHydrateInput } from './engine/requestBuilders'
import { buildWidgetSessionDataFromResult, type DashboardViewState, type WidgetSessionState } from './engine/state'

// Data preparation for the dashboard PDF: tables print every row, so a table whose session
// holds only one server page is re-fetched in full (page 1, every row, no preview row limit).

export type PdfRowCoverage = { loaded: number; total: number }

export function isPdfTableWidget(widget: Pick<DashboardWidget, 'type'>) {
  return widget.type === 'table' || widget.type === 'dataBarTable' || widget.type === 'pivotTable'
}

/** True when a table widget's session holds fewer rows than the server reports (server-side pagination). */
export function needsFullTableFetch(widget: Pick<DashboardWidget, 'type'>, session: WidgetSessionState | undefined) {
  if (!session || !isPdfTableWidget(widget)) return false
  // An engine drilldown session comes from the drilldown endpoint; the hydrate request would not reproduce it.
  if (session.query.drilldown) return false
  const { hasPaginationMetadata, totalRows, rows } = session.data
  return hasPaginationMetadata && totalRows > rows.length
}

/**
 * The widget's current request (applied filters, sort, dimensions, measure parameters), widened
 * to one page holding every row, with the preview row limit off. Same widening as the per-widget
 * "Download Visuals Data" export.
 */
export function buildFullTableHydrateInput(
  state: DashboardViewState,
  widgetId: string,
  clientId: string,
): HydrateWidgetInput | null {
  const session = state.widgets[widgetId]
  const intent = createWidgetRequestIntent(state, widgetId, 'refresh-all')
  if (!session || !intent) return null

  const pageSize = Math.max(session.data.totalRows, session.data.rows.length, 1)
  const input = buildWidgetHydrateInput(state, widgetId, { ...intent, query: { ...intent.query, page: 1, pageSize } }, clientId)
  if (!input) return null
  return { ...input, page: 1, pageSize, applyPreviewRowLimit: false }
}

export type FullTableDataResult = {
  widgetSessions: Record<string, WidgetSessionState>
  /** Loaded vs total rows for every table widget; loaded < total only when a full fetch failed. */
  rowCoverage: Record<string, PdfRowCoverage>
  failedWidgetIds: string[]
}

/**
 * Returns `widgetSessions` with each partial table replaced by its full data. The input sessions
 * (a snapshot clone) are not mutated, and engine state is never touched. A failed fetch keeps
 * the loaded rows and is reported in `failedWidgetIds`.
 */
export async function fetchFullTableData({
  state,
  clientId,
  widgetSessions,
  fetchWidget = hydrateWidgetData,
}: {
  state: DashboardViewState
  clientId: string
  widgetSessions: Record<string, WidgetSessionState>
  fetchWidget?: (input: HydrateWidgetInput) => Promise<HydratedWidgetResult>
}): Promise<FullTableDataResult> {
  const widgets = state.dashboard?.widgets ?? []
  const nextSessions: Record<string, WidgetSessionState> = { ...widgetSessions }
  const failedWidgetIds: string[] = []

  const targets = widgets.filter((widget) => needsFullTableFetch(widget, widgetSessions[widget.id]))
  const results = await Promise.allSettled(targets.map(async (widget) => {
    const input = buildFullTableHydrateInput(state, widget.id, clientId)
    if (!input) throw new Error(`Could not build the full-data request for widget ${widget.id}.`)
    return { widgetId: widget.id, result: await fetchWidget(input) }
  }))

  results.forEach((outcome, index) => {
    const widgetId = targets[index]?.id
    if (!widgetId) return
    const session = widgetSessions[widgetId]
    if (outcome.status === 'rejected' || !session) {
      failedWidgetIds.push(widgetId)
      return
    }
    nextSessions[widgetId] = {
      ...session,
      data: {
        ...buildWidgetSessionDataFromResult(outcome.value.result),
        comparisonChartData: session.data.comparisonChartData,
        lastUpdatedAt: session.data.lastUpdatedAt,
      },
    }
  })

  const rowCoverage: Record<string, PdfRowCoverage> = {}
  widgets.forEach((widget) => {
    const session = nextSessions[widget.id]
    if (!session || !isPdfTableWidget(widget)) return
    const loaded = session.data.rows.length
    const total = session.data.hasPaginationMetadata ? Math.max(session.data.totalRows, loaded) : loaded
    rowCoverage[widget.id] = { loaded, total }
  })

  return { widgetSessions: nextSessions, rowCoverage, failedWidgetIds }
}
